/* ==========================================================================
   Lumira Animations Loader
   - Auto-tags Lumira sections (.lumira-section, .lumira-hero, .lumira-footer,
     .lumira-header) and Shopify sections matching home-/prelaunch-/support-
     with data-lum-anim attributes.
   - Uses IntersectionObserver to add .is-revealed when in view.
   - Respects prefers-reduced-motion (skips entirely).
   - Idempotent + reruns on Shopify section load events.
   ========================================================================== */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  const REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Section selectors that should receive auto-animations
  const LUMIRA_SECTION_SELECTOR = [
    '.lumira-section',
    '.lumira-hero',
    '.lumira-footer',
    '.lumira-header',
    '.shopify-section[id*="home-"]',
    '.shopify-section[id*="prelaunch-"]',
    '.shopify-section[id*="support-"]',
    '.shopify-section[id*="lumira-"]'
  ].join(',');

  // Element-level rules: selector -> {anim, delay (optional)}
  // These are evaluated within each Lumira section; first match wins.
  const RULES = [
    // Eyebrows: gentle fade with shimmer (CSS handles shimmer)
    { sel: '.lh-eyebrow,.lp-eyebrow,.ld-eyebrow,.lr-eyebrow,.lc-eyebrow,.lcb-eyebrow,.lf-eyebrow,.lrv-eyebrow,.lo-eyebrow,.lib-label', anim: 'fade-up' },

    // Headings — main reveal
    { sel: 'h1,h2,h3,.lh-h2,.lp-h2,.ld-h2,.lr-h2,.lc-h2,.lcb-h2,.lf-h2,.lrv-h2,.lo-h2,.lh-name,.lr-name,.lh-step-title,.lo-step-title,.h1.h1,.h2.h2,.h3.h3', anim: 'fade-up' },

    // Body copy / supporting text
    { sel: 'p,.lh-tagline,.lh-step-body,.lr-quote,.lrv-quote,.lp-body,.lo-sub,.lib-desc,.ld-body,.lc-body,.lf-body', anim: 'fade-up' },

    // Buttons / CTAs
    { sel: '.lh-cta,.lo-button,.lhd-cta,.button,.btn-primary,.btn,.btn--primary', anim: 'fade-up' },

    // Pricing / save badges
    { sel: '.lh-save,.lo-save', anim: 'scale-in' },
    { sel: '.lh-price,.lo-price,.lh-was,.lo-was', anim: 'fade-up' },

    // Cards, steps, list items
    { sel: '.lh-bullet,.lh-step,.lo-step,.lr-card,.lrv-card,.lf-col,.lib-cell,.lcb-row,.lp-card,.ld-card,.lc-row,.lf-faq', anim: 'fade-up' },

    // Comparison rows — slide from left
    { sel: '.lcb-row,.lc-row', anim: 'slide-right' },

    // Images / media — slide-in from sides depending on alignment
    { sel: '.lh-img,.lh-media,.ld-img,.lr-img,.lo-img,.lp-img,.lib-img', anim: 'zoom-in' },

    // Generic media inside Lumira sections
    { sel: 'picture,video,.media,.image-block', anim: 'fade-up' }
  ];

  // Map section names → preferred image direction
  const IMG_DIRECTION_BY_SECTION = [
    { test: /lumira-hero|home-01|prelaunch-02/, anim: 'slide-left' },
    { test: /lumira-problem-serums/,            anim: 'slide-right' },
    { test: /lumira-dermatologist/,             anim: 'slide-left' },
    { test: /lumira-before-after|home-05/,      anim: 'zoom-in' },
    { test: /lumira-how-it-works/,              anim: 'fade-up' },
    { test: /lumira-reviews|home-07/,           anim: 'fade-up' }
  ];

  function pickImageAnim(sectionEl) {
    const id = (sectionEl.id || '') + ' ' + (sectionEl.className || '');
    for (const r of IMG_DIRECTION_BY_SECTION) {
      if (r.test.test(id)) return r.anim;
    }
    return 'zoom-in';
  }

  // Decorate a single section with anim attributes (idempotent)
  function decorateSection(sectionEl) {
    if (!sectionEl || sectionEl.dataset.lumDecorated === '1') return;
    sectionEl.dataset.lumDecorated = '1';

    const imageAnim = pickImageAnim(sectionEl);

    // Pass each rule but only set if element has no [data-lum-anim] yet
    RULES.forEach(rule => {
      const nodes = sectionEl.querySelectorAll(rule.sel);
      let counter = 0;
      nodes.forEach(node => {
        if (node.hasAttribute('data-lum-anim')) return;
        if (node.closest('[data-lum-skip]')) return;

        // Override anim for image containers based on section direction
        let anim = rule.anim;
        if (rule.sel.includes('lh-img') || rule.sel.includes('lh-media') ||
            rule.sel.includes('ld-img') || rule.sel.includes('lr-img') ||
            rule.sel.includes('lo-img') || rule.sel.includes('lp-img') ||
            rule.sel.includes('lib-img')) {
          anim = imageAnim;
        }

        node.setAttribute('data-lum-anim', anim);

        // Apply small stagger inside groups of similar elements
        if (counter > 0 && counter <= 8) {
          node.setAttribute('data-lum-delay', String(counter));
        }
        counter++;
      });
    });

    // Mark items inside flex/grid containers with their own stagger
    const STAGGER_GROUPS = '.lh-bullets,.lh-steps,.lo-steps,.lib-grid,.lr-grid,.lrv-grid,.lf-cols,.lcb-table,.lc-table,.lp-grid,.ld-grid';
    sectionEl.querySelectorAll(STAGGER_GROUPS).forEach(group => {
      const children = group.children;
      let i = 0;
      for (const child of children) {
        if (!child.hasAttribute('data-lum-anim')) {
          child.setAttribute('data-lum-anim', 'fade-up');
        }
        child.setAttribute('data-lum-delay', String(Math.min(i, 8)));
        i++;
      }
    });
  }

  // Find all Lumira sections currently in the DOM
  function getSections(root) {
    return (root || document).querySelectorAll(LUMIRA_SECTION_SELECTOR);
  }

  // Reveal observer
  let observer;
  function ensureObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      // Trigger slightly before fully in view
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.05
    });
    return observer;
  }

  function observeAll(root) {
    const obs = ensureObserver();
    const targets = (root || document).querySelectorAll('[data-lum-anim]:not(.is-revealed)');
    targets.forEach(t => obs.observe(t));
  }

  function revealAllImmediately(root) {
    const targets = (root || document).querySelectorAll('[data-lum-anim]:not(.is-revealed)');
    targets.forEach(t => t.classList.add('is-revealed'));
  }

  // Run pipeline
  function run(root) {
    if (REDUCED) {
      // Skip animations completely; reveal everything for safety
      const sections = getSections(root);
      sections.forEach(decorateSection);
      revealAllImmediately(root);
      return;
    }
    const sections = getSections(root);
    sections.forEach(decorateSection);
    observeAll(root);
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => run());
  } else {
    run();
  }

  // Re-run on Shopify section reload (Theme Editor) and on dynamic content
  document.addEventListener('shopify:section:load', (e) => run(e.target));
  document.addEventListener('shopify:section:select', (e) => run(e.target));
  document.addEventListener('shopify:block:load', (e) => run(e.target));

  // SPA-ish navigation safety: re-run on history changes
  window.addEventListener('pageshow', () => run());

  // Expose for debugging / manual re-runs
  window.LumiraAnim = { run, revealAll: revealAllImmediately, _RULES: RULES };
})();
